import { Button, Icon, Header, Modal, Input, DatePicker, Checkbox } from '@scuf/common';
import { DataTable } from '@scuf/datatable';
import { RoleComponent } from 'components/roleComponent';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { notificationAtom, REPAIR_CENTER } from 'resources';
import { useSetRecoilState } from 'recoil';
import useFetch from 'helpers/hooks/useFetch';
import useUser from 'helpers/hooks/useUser';
import { Coupon, FilterCoupons } from 'models/repaircenter/coupon';

function RmaCoupons() {
    const { t } = useTranslation(['repaircenter', 'common', 'pages']);
    const InitialFilter = {
        name: '',
        pageNumber: 1,
        pageSize: 10
    };
    const [filters, setFilters] = useState(InitialFilter);
    const [filteredData, setFilteredData] = useState([]);
    const [totalItems, setTotalItems] = useState(0);
    const { user: userDetails } = useUser();
    const setNotification = useSetRecoilState(notificationAtom);
    const [newName, setNewName] = useState<string>();
    const [newDescription, setNewDescription] = useState<string>();
    const [newValidity, setNewValidity] = useState<Date>();
    const [idCoupon, setIdCoupon] = useState<number>(0);
    const [modalAdd, setModalAdd] = useState(false);
    const [modalDelete, setModalDelete] = useState(false);
    const [modalEdit, setModalEdit] = useState(false);
    const [couponEdit, setCouponEdit] = useState<Coupon>();

    const [, addCoupon, loadingCreating] = useFetch<Coupon, any>({
        url: REPAIR_CENTER.addCoupon,
        method: 'POST',
        onError: t('notification:critical.Create')
    });

    const [, editCoupon, loadingEdit] = useFetch<Coupon, any>({
        url: REPAIR_CENTER.updateCoupon,
        method: 'PUT',
        onError: t('notification:critical.Create')
    });

    const [, getCoupons, loadingGetCoupons] = useFetch<FilterCoupons, Coupon[]>({
        url: REPAIR_CENTER.getCoupons,
        method: 'GET',
        onError: t('common:errors.Unexpected')
    });

    const [, activeOrInactiveCoupons, loagingActiveOrInactive] = useFetch<number, any>({
        url: REPAIR_CENTER.activeOrInactiveCoupon,
        method: 'PUT',
        onError: t('common:errors.Unexpected')
    });

    const [, couponDelete, loadingDelete] = useFetch<number, ConstrainBoolean>({
        url: `${REPAIR_CENTER.deleteCoupon}/${idCoupon}`,
        method: 'DELETE',
        onError: t('common:errors.Unexpected')
    });

    useEffect(() => {
        getFilteredCoupons(InitialFilter);
    }, []);

    const resetFilters = () => {
        setFilters(InitialFilter);
        getFilteredCoupons(InitialFilter);
    };

    const getFilteredCoupons = async (filter: FilterCoupons) => {
        await getCoupons(filter, (current: any) => {
            if (current.data) {
                setFilteredData(current.data);
                setTotalItems(current.data.length);
            }
        });
    };

    const applyFilters = () => {
        getFilteredCoupons(filters);
    };

    const handleActiveOrInactiveCoupon = async (id: number) => {
        try {
            await activeOrInactiveCoupons(id);
            setNotification(old => ({ ...old, message: t('repaircenter:rma.coupons.SuccessStatus'), type: 'success' }));
            getFilteredCoupons(filters);
        } catch {
            setNotification(old => ({ ...old, message: t('repaircenter:rma.coupons.ErrorStatus'), type: 'success' }));
        }
    };

    const deleteCoupon = async () => {
        try {
            await couponDelete();
            setModalDelete(false);
            getFilteredCoupons(filters);
            setNotification(old => ({ ...old, message: t('repaircenter:rma.coupons.SuccessDelete'), type: 'success' }));
        } catch {
            setNotification(old => ({ ...old, message: t('repaircenter:rma.coupons.ErrorDelete'), type: 'critical' }));
        }
    };

    const handleAddCoupon = async () => {
        if (!newName?.trim() || !newDescription?.trim() || !newValidity) return;

        const newCoupon: Coupon = {
            id: 0,
            name: newName,
            description: newDescription,
            validity: newValidity,
            active: true,
            ssoId: userDetails?.ssoid ?? '',
            createdAt: new Date(),
            updatedAt: new Date()
        };

        try {
            await addCoupon(newCoupon);
            setNotification(old => ({ ...old, message: t('repaircenter:rma.coupons.SuccessAdd'), type: 'success' }));
            setNewName('');
            setNewDescription('');
            setNewValidity(undefined);
            setModalAdd(false);
            getFilteredCoupons(filters);
        } catch {
            setNotification(old => ({ ...old, message: t('repaircenter:rma.coupons.ErrorAdd'), type: 'critical' }));
        }
    };

    const handleEditCoupon = async () => {
        if (!newName?.trim() || !newDescription?.trim() || !newValidity || !couponEdit?.id) return;

        const newCoupon: Coupon = {
            id: couponEdit.id,
            name: newName,
            description: newDescription,
            validity: newValidity,
            active: couponEdit.active,
            ssoId: userDetails?.ssoid ?? '',
            createdAt: couponEdit.createdAt,
            updatedAt: new Date()
        };

        try {
            await editCoupon(newCoupon);
            setNotification(old => ({ ...old, message: t('repaircenter:rma.coupons.SuccessUpdate'), type: 'success' }));
            setNewName('');
            setNewDescription('');
            setNewValidity(undefined);
            setCouponEdit(undefined);
            setModalEdit(false);
            getFilteredCoupons(filters);
        } catch {
            setNotification(old => ({ ...old, message: t('repaircenter:rma.coupons.ErrorUpdate'), type: 'critical' }));
        }
    };

    const searchFieldsRender = () => {
        return (
            <div>
                <Header title={t('pages:Coupons')} logo={false} menu={false}>
                    <div style={{ margin: '10px' }}>
                        <RoleComponent roles={['SUPERADMIN']}>
                            <Button
                                content={t('repaircenter:rma.coupons.AddCoupons')}
                                type="primary"
                                size="medium"
                                iconPosition="right"
                                icon={<Icon name="add" root="building" />}
                                onClick={() => setModalAdd(true)}
                                description={t('repaircenter:rma.coupons.AddCoupons')}
                                disabled={loagingActiveOrInactive || loadingGetCoupons}
                                loading={loagingActiveOrInactive || loadingGetCoupons}
                            />
                        </RoleComponent>
                    </div>
                </Header>
                <div className="form4Columns">
                    <Input
                        placeholder={t('repaircenter:rma.coupons.NameCoupon')}
                        label={t('repaircenter:rma.coupons.NameCoupon')}
                        onChange={value => setFilters({ ...filters, name: value })}
                        value={filters.name}
                    />
                </div>
                <div className="buttonsSearch">
                    <Button
                        type="secondary"
                        icon="badge-delete"
                        content={t('common:actions.ResetFilters')}
                        iconPosition="right"
                        onClick={resetFilters}
                        disabled={loagingActiveOrInactive || loadingGetCoupons}
                        loading={loagingActiveOrInactive || loadingGetCoupons}
                    />
                    <Button
                        type="primary"
                        icon="filter1"
                        content={t('common:actions.ApplyFilters')}
                        iconPosition="right"
                        onClick={applyFilters}
                        disabled={loagingActiveOrInactive || loadingGetCoupons}
                        loading={loagingActiveOrInactive || loadingGetCoupons}
                    />
                </div>
            </div>
        );
    };

    const tableCouponRender = () => {
        return (
            <div style={{ marginTop: '26px', marginLeft: '20px' }}>
                <DataTable
                    columnResizeMode="fit"
                    data={filteredData ?? []}
                    scrollable={true}
                    scrollWidth="100%"
                    resizableColumns={true}
                    loading={loadingGetCoupons || loagingActiveOrInactive}
                >
                    <DataTable.Column field={'id'} align="center" header={t('Id')} sortable={true} />
                    <DataTable.Column
                        field={'name'}
                        align="center"
                        header={t('repaircenter:rma.coupons.NameCoupon')}
                        sortable={true}
                    />
                    <DataTable.Column
                        field={'description'}
                        align="center"
                        header={t('repaircenter:rma.coupons.DescriptionCoupon')}
                        sortable={true}
                    />
                    <DataTable.Column
                        field={'validity'}
                        align="center"
                        header={t('repaircenter:rma.coupons.ValidityCoupon')}
                        sortable={true}
                    />
                    <DataTable.Column
                        field={'active'}
                        align="center"
                        header={t('repaircenter:rma.coupons.Status')}
                        sortable={true}
                        renderer={e => (
                            <Checkbox
                                checked={e.rowData.active}
                                toggle={true}
                                onChange={async (value: boolean) => {
                                    handleActiveOrInactiveCoupon(e.rowData.id);
                                }}
                                disabled={loagingActiveOrInactive || loadingGetCoupons}
                            />
                        )}
                    />
                    <DataTable.Column
                        field="Edit"
                        renderer={e => (
                            <Icon
                                size="small"
                                root="common"
                                name="edit"
                                rotated={190}
                                className="icon-event"
                                disabled={userDetails?.role}
                                onClick={() => {
                                    setNewName(e.rowData.name);
                                    setNewDescription(e.rowData.description);
                                    setNewValidity(e.rowData.validity);
                                    setCouponEdit(e.rowData);
                                    setModalEdit(true);
                                }}
                            />
                        )}
                        align="center"
                        header={t('common:translation.Edit')}
                    />
                    <DataTable.Column
                        field="Delete"
                        renderer={e => (
                            <Icon
                                size="small"
                                root="common"
                                name="delete"
                                rotated={190}
                                className="icon-event"
                                disabled={userDetails?.role}
                                onClick={() => {
                                    setIdCoupon(e.rowData.id);
                                    setModalDelete(true);
                                }}
                            />
                        )}
                        align="center"
                        header={t('common:actions.Delete')}
                    />
                </DataTable>
                <DataTable.Pagination
                    totalItems={totalItems}
                    itemsPerPage={10}
                    onPageChange={pageNumber => {
                        const updatedFilters = { ...filters, pageNumber: Number(pageNumber) };
                        getFilteredCoupons(updatedFilters);
                    }}
                />
            </div>
        );
    };

    const renderModalEliminateCoupon = () => {
        return (
            <Modal
                open={modalDelete}
                closeIcon={true}
                onClose={() => setModalDelete(false)}
                closeOnDimmerClick={true}
                size="small"
                className="modal-excluir-content"
            >
                <Modal.Header>
                    <h1>{t('common:actions.Delete')}</h1>
                </Modal.Header>
                <Modal.Content>
                    <p>{t('repaircenter:rma.coupons.DeleteCoupon')}</p>
                </Modal.Content>

                <Modal.Footer>
                    <Button
                        type="primary"
                        content={t('common:translation.Confirm')}
                        onClick={() => {
                            deleteCoupon();
                        }}
                        loading={loadingDelete}
                        disabled={loadingDelete}
                    />
                </Modal.Footer>
            </Modal>
        );
    };

    const renderModalAddCoupon = () => {
        return (
            <Modal
                open={modalAdd}
                closeIcon={true}
                onClose={() => setModalAdd(false)}
                closeOnDimmerClick={true}
                size="small"
                className="modal-excluir-content"
            >
                <Modal.Header>
                    <h1>{t('repaircenter:rma.coupons.AddCoupons')}</h1>
                </Modal.Header>
                <Modal.Content>
                    <Input
                        placeholder={t('repaircenter:rma.coupons.NameCoupon')}
                        label={t('repaircenter:rma.coupons.NameCoupon')}
                        value={newName}
                        onChange={(value: any) => setNewName(value)}
                    />
                    <Input
                        placeholder={t('repaircenter:rma.coupons.DescriptionCoupon')}
                        label={t('repaircenter:rma.coupons.DescriptionCoupon')}
                        value={newDescription}
                        onChange={(value: any) => setNewDescription(value)}
                    />
                    <DatePicker
                        reserveSpace={false}
                        value={newValidity ? new Date(newValidity) : ''}
                        onChange={value => setNewValidity(value)}
                        label={t('repaircenter:rma.coupons.ValidityCoupon')}
                        type={'date'}
                        displayFormat="DD/MM/YYYY"
                    />
                </Modal.Content>
                <Modal.Footer>
                    <Button
                        type="primary"
                        content={t('common:actions.Cancel')}
                        onClick={() => {
                            setNewName('');
                            setNewDescription('');
                            setNewValidity(undefined);
                            setModalAdd(false);
                        }}
                        loading={loadingCreating}
                        disabled={loadingCreating}
                    />
                    <Button
                        type="primary"
                        content={t('common:translation.Confirm')}
                        onClick={() => {
                            handleAddCoupon();
                        }}
                        loading={loadingCreating}
                        disabled={!newName?.trim() || !newDescription?.trim() || !newValidity || loadingCreating}
                    />
                </Modal.Footer>
            </Modal>
        );
    };

    const renderModalEditCoupon = () => {
        return (
            <Modal
                open={modalEdit}
                closeIcon={true}
                onClose={() => setModalEdit(false)}
                closeOnDimmerClick={true}
                size="small"
                className="modal-excluir-content"
            >
                <Modal.Header>
                    <h1>{t('repaircenter:rma.coupons.EditCoupon')}</h1>
                </Modal.Header>
                <Modal.Content>
                    <Input
                        placeholder={t('repaircenter:rma.coupons.NameCoupon')}
                        label={t('repaircenter:rma.coupons.NameCoupon')}
                        value={newName}
                        onChange={(value: any) => setNewName(value)}
                    />
                    <Input
                        placeholder={t('repaircenter:rma.coupons.DescriptionCoupon')}
                        label={t('repaircenter:rma.coupons.DescriptionCoupon')}
                        value={newDescription}
                        onChange={(value: any) => setNewDescription(value)}
                    />
                    <DatePicker
                        reserveSpace={false}
                        value={newValidity ? new Date(newValidity) : ''}
                        onChange={value => setNewValidity(value)}
                        label={t('repaircenter:rma.coupons.ValidityCoupon')}
                        type={'date'}
                        displayFormat="DD/MM/YYYY"
                    />
                </Modal.Content>
                <Modal.Footer>
                    <Button
                        type="primary"
                        content={t('common:actions.Cancel')}
                        onClick={() => {
                            setNewName('');
                            setNewDescription('');
                            setNewValidity(undefined);
                            setModalEdit(false);
                        }}
                        loading={loadingEdit}
                        disabled={loadingEdit}
                    />
                    <Button
                        type="primary"
                        content={t('common:translation.Confirm')}
                        onClick={() => {
                            handleEditCoupon();
                        }}
                        loading={loadingEdit}
                        disabled={!newName?.trim() || !newDescription?.trim() || !newValidity || loadingEdit}
                    />
                </Modal.Footer>
            </Modal>
        );
    };

    return (
        <>
            {searchFieldsRender()}
            {tableCouponRender()}
            {renderModalAddCoupon()}
            {renderModalEliminateCoupon()}
            {renderModalEditCoupon()}
        </>
    );
}

export default RmaCoupons;
